import React, { useState, useEffect } from 'react';
import {
    ArrowLeft, Save, Eye, Plus, Trash2, MapPin, Package, Truck,
    Users, Building2, Calendar, Clock, DollarSign, AlertTriangle,
    CheckCircle, Upload, FileText, Map as MapIcon, Navigation,
    Phone, Mail, Hash, Ruler, Timer, Container, ChevronDown, ChevronUp,
    User, Car, Clipboard, Settings, Globe, Shield, X, Thermometer,
    Camera, Bell, Target, Factory, Ship, Warehouse
} from 'lucide-react';

const CreateJobPage = () => {
    const [jobData, setJobData] = useState({});
    const [loading, setLoading] = useState(false);
    const [estimatedCost, setEstimatedCost] = useState(0);
    const [estimatedDuration, setEstimatedDuration] = useState(0);
    const [legs, setLegs] = useState([{
        id: 1,
        expanded: true,
        type: 'pickup',
        customer: 'CUST001',
        vendor: 'VEND001',
        port: 'PORT001',
        warehouse: 'WH001',
        transporter: 'TRANS001',
        vehicle: 'VEH001',
        driver: 'DRV001',
        origin: 'Mumbai Port Trust, Gate No. 4, Mumbai - 400001',
        destination: 'Delhi North Warehouse, Sector 15, Gurgaon - 122001',
        vehicleType: 'container_truck',
        goodsType: 'electronics',
        container: 'CONT001',
        distance: 1424,
        estimatedHours: 36,
        journeyDate: '2025-06-15',
        journeyTime: '08:00',
        temperatureMonitoring: false,
        temperatureRange: '-5 to 25°C',
        gpsTracking: true,
        photoUpdates: true,
        geofencing: true,
        trackingRequired: true,
        specialInstructions: 'Handle with care - fragile electronics'
    }]);

    const [formData, setFormData] = useState({
        type: 'import',
        priority: 'high',
        jobNumber: 'TT-2025-001',
        customerPO: 'PO-TATA-2025-0156',
        description: 'Electronics shipment from Mumbai to Delhi warehouse'
    });

    // Complete demo data with Indian context
    const customers = [
        { value: 'CUST001', label: 'Tata Motors Ltd', contact: 'Rajesh Kumar', phone: '+91-9876543210', email: 'rajesh.kumar@tatamotors.com' },
        { value: 'CUST002', label: 'Maruti Suzuki India Ltd', contact: 'Priya Sharma', phone: '+91-9876543211', email: 'priya.sharma@maruti.com' },
        { value: 'CUST003', label: 'Infosys Limited', contact: 'Amit Singh', phone: '+91-9876543212', email: 'amit.singh@infosys.com' },
        { value: 'CUST004', label: 'Coal India Ltd', contact: 'Sunita Reddy', phone: '+91-9876543213', email: 'sunita.reddy@coalindia.in' },
        { value: 'CUST005', label: 'Reliance Industries', contact: 'Vikash Gupta', phone: '+91-9876543214', email: 'vikash.gupta@ril.com' }
    ];

    const vendors = [
        { value: 'VEND001', label: 'Mumbai Port Trust', contact: 'Mahesh Patil', phone: '+91-9876543220', type: 'Port Authority' },
        { value: 'VEND002', label: 'Chennai Logistics Pvt Ltd', contact: 'Lakshmi Menon', phone: '+91-9876543221', type: 'Logistics Provider' },
        { value: 'VEND003', label: 'Delhi Transport Co', contact: 'Harinder Singh', phone: '+91-9876543222', type: 'Transport Company' },
        { value: 'VEND004', label: 'Gujarat Logistics Hub', contact: 'Kiran Patel', phone: '+91-9876543223', type: 'Warehouse Provider' }
    ];

    const ports = [
        { value: 'PORT001', label: 'Mumbai Port (JNPT)', location: 'Navi Mumbai, Maharashtra', code: 'INNSA' },
        { value: 'PORT002', label: 'Chennai Port', location: 'Chennai, Tamil Nadu', code: 'INMAA' },
        { value: 'PORT003', label: 'Kandla Port', location: 'Kandla, Gujarat', code: 'INKDL' },
        { value: 'PORT004', label: 'Cochin Port', location: 'Kochi, Kerala', code: 'INCOK' },
        { value: 'PORT005', label: 'Visakhapatnam Port', location: 'Visakhapatnam, Andhra Pradesh', code: 'INVTZ' }
    ];

    const warehouses = [
        { value: 'WH001', label: 'Delhi North Warehouse', address: 'Sector 15, Industrial Area, Gurgaon - 122001', capacity: '50,000 sq ft' },
        { value: 'WH002', label: 'Mumbai Central Warehouse', address: 'Plot 123, MIDC, Andheri East, Mumbai - 400093', capacity: '75,000 sq ft' },
        { value: 'WH003', label: 'Chennai Port Warehouse', address: 'North Port Area, Chennai Port - 600001', capacity: '40,000 sq ft' },
        { value: 'WH004', label: 'Bangalore Tech Warehouse', address: 'Electronic City Phase 1, Bangalore - 560100', capacity: '60,000 sq ft' },
        { value: 'WH005', label: 'Kolkata East Warehouse', address: 'Salt Lake Sector V, Kolkata - 700091', capacity: '35,000 sq ft' }
    ];

    const transporters = [
        { value: 'TRANS001', label: 'Blue Dart Express Ltd', code: 'BDE', contact: 'Rajesh Kumar', phone: '+91-9876543230', rating: '4.8/5' },
        { value: 'TRANS002', label: 'Gati Ltd', code: 'GTI', contact: 'Priya Sharma', phone: '+91-9876543231', rating: '4.6/5' },
        { value: 'TRANS003', label: 'TCI Express Ltd', code: 'TCI', contact: 'Amit Singh', phone: '+91-9876543232', rating: '4.7/5' },
        { value: 'TRANS004', label: 'VRL Logistics', code: 'VRL', contact: 'Sunita Reddy', phone: '+91-9876543233', rating: '4.5/5' },
        { value: 'TRANS005', label: 'Mahindra Logistics', code: 'MLL', contact: 'Kiran Patel', phone: '+91-9876543234', rating: '4.9/5' }
    ];

    const vehicles = [
        { value: 'VEH001', label: 'MH-01-AB-1234', type: 'Tata 407', capacity: '25T', driver: 'Ravi Kumar', phone: '+91-9876543240', status: 'Available' },
        { value: 'VEH002', label: 'DL-02-XY-9876', type: 'Ashok Leyland 2518', capacity: '40T', driver: 'Suresh Yadav', phone: '+91-9876543241', status: 'Available' },
        { value: 'VEH003', label: 'TN-09-BC-5432', type: 'Volvo FH12', capacity: '25T', driver: 'Murugan S', phone: '+91-9876543242', status: 'In Transit' },
        { value: 'VEH004', label: 'WB-03-CD-7890', type: 'Mahindra Blazo X', capacity: '40T', driver: 'Bikash Das', phone: '+91-9876543243', status: 'Available' },
        { value: 'VEH005', label: 'GJ-05-EF-6543', type: 'Eicher Pro 8049', capacity: '49T', driver: 'Kiran Patel', phone: '+91-9876543244', status: 'Maintenance' }
    ];

    const drivers = [
        { value: 'DRV001', label: 'Ravi Kumar - +91-9876543240', phone: '+91-9876543240', license: 'DL-1420110012345', experience: '8 years', rating: '4.8/5' },
        { value: 'DRV002', label: 'Suresh Yadav - +91-9876543241', phone: '+91-9876543241', license: 'DL-0720110012346', experience: '12 years', rating: '4.6/5' },
        { value: 'DRV003', label: 'Murugan S - +91-9876543242', phone: '+91-9876543242', license: 'TN-0320110012347', experience: '6 years', rating: '4.7/5' },
        { value: 'DRV004', label: 'Bikash Das - +91-9876543243', phone: '+91-9876543243', license: 'WB-1920110012348', experience: '10 years', rating: '4.5/5' },
        { value: 'DRV005', label: 'Kiran Patel - +91-9876543244', phone: '+91-9876543244', license: 'GJ-1520110012349', experience: '15 years', rating: '4.9/5' }
    ];

    const containers = [
        {
            value: 'CONT001',
            label: 'MSKU-123456-7',
            type: '20ft',
            owner: 'Maersk Line',
            tare: 2200,
            maxWeight: 24000,
            status: 'Available',
            location: 'Mumbai Port',
            freeTime: 72,
            detentionRate: 500
        },
        {
            value: 'CONT002',
            label: 'MSCU-789012-3',
            type: '40ft',
            owner: 'MSC',
            tare: 3800,
            maxWeight: 30480,
            status: 'Available',
            location: 'Chennai Port',
            freeTime: 96,
            detentionRate: 750
        },
        {
            value: 'CONT003',
            label: 'CMAU-345678-9',
            type: '20ft',
            owner: 'CMA CGM',
            tare: 2180,
            maxWeight: 24000,
            status: 'In Use',
            location: 'Kandla Port',
            freeTime: 72,
            detentionRate: 450
        },
        {
            value: 'CONT004',
            label: 'COSU-901234-5',
            type: '40hq',
            owner: 'COSCO',
            tare: 4200,
            maxWeight: 30480,
            status: 'Available',
            location: 'Mumbai Port',
            freeTime: 120,
            detentionRate: 800
        }
    ];

    const vehicleTypes = [
        { value: 'container_truck', label: 'Container Truck', icon: '🚛' },
        { value: 'flatbed', label: 'Flatbed Truck', icon: '🚚' },
        { value: 'refrigerated', label: 'Refrigerated Truck', icon: '❄️' },
        { value: 'tanker', label: 'Tanker Truck', icon: '🚰' },
        { value: 'car_carrier', label: 'Car Carrier', icon: '🚗' },
        { value: 'heavy_duty', label: 'Heavy Duty Truck', icon: '🏗️' }
    ];

    const goodsTypes = [
        { value: 'electronics', label: 'Electronics & Technology', icon: '📱' },
        { value: 'automotive', label: 'Automotive Parts', icon: '🔧' },
        { value: 'textiles', label: 'Textiles & Garments', icon: '👕' },
        { value: 'chemicals', label: 'Chemicals & Pharmaceuticals', icon: '⚗️' },
        { value: 'food_beverage', label: 'Food & Beverages', icon: '🍽️' },
        { value: 'machinery', label: 'Machinery & Equipment', icon: '⚙️' },
        { value: 'raw_materials', label: 'Raw Materials', icon: '🏭' },
        { value: 'consumer_goods', label: 'Consumer Goods', icon: '📦' }
    ];

    // Calculate costs and duration
    useEffect(() => {
        const totalDistance = legs.reduce((sum, leg) => sum + (leg.distance || 0), 0);
        const totalHours = legs.reduce((sum, leg) => sum + (leg.estimatedHours || 0), 0);

        // Cost calculation based on distance, vehicle type, and goods type
        const baseCostPerKm = 18; // Base rate per km
        const calculatedCost = totalDistance * baseCostPerKm;
        setEstimatedCost(calculatedCost);
        setEstimatedDuration(totalHours);
    }, [legs]);

    const updateFormData = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const addLeg = () => {
        const newLeg = {
            id: legs.length + 1,
            expanded: true,
            type: legs.length === 0 ? 'pickup' : 'delivery',
            customer: '',
            vendor: '',
            port: '',
            warehouse: '',
            transporter: '',
            vehicle: '',
            driver: '',
            origin: '',
            destination: '',
            vehicleType: 'container_truck',
            goodsType: 'electronics',
            container: '',
            distance: 0,
            estimatedHours: 0,
            journeyDate: '',
            journeyTime: '',
            temperatureMonitoring: false,
            temperatureRange: '',
            gpsTracking: true,
            photoUpdates: true,
            geofencing: true,
            trackingRequired: true,
            specialInstructions: ''
        };
        setLegs([...legs, newLeg]);
    };

    const updateLeg = (index, field, value) => {
        const updatedLegs = legs.map((leg, i) => {
            if (i === index) {
                const updatedLeg = { ...leg, [field]: value };

                // Auto-populate related fields
                if (field === 'transporter') {
                    const transporter = transporters.find(t => t.value === value);
                    if (transporter) {
                        updatedLeg.transporterContact = transporter.contact;
                        updatedLeg.transporterPhone = transporter.phone;
                    }
                }

                if (field === 'vehicle') {
                    const vehicle = vehicles.find(v => v.value === value);
                    if (vehicle) {
                        updatedLeg.driver = vehicle.driver;
                        updatedLeg.driverPhone = vehicle.phone;
                        updatedLeg.vehicleCapacity = vehicle.capacity;
                    }
                }

                if (field === 'container') {
                    const container = containers.find(c => c.value === value);
                    if (container) {
                        updatedLeg.containerType = container.type;
                        updatedLeg.containerOwner = container.owner;
                        updatedLeg.containerLocation = container.location;
                        updatedLeg.freeTime = container.freeTime;
                        updatedLeg.detentionRate = container.detentionRate;
                    }
                }

                // Auto-calculate estimated hours based on distance (average 40 km/hr)
                if (field === 'distance' && value > 0) {
                    updatedLeg.estimatedHours = Math.round((value / 40) * 10) / 10;
                }

                return updatedLeg;
            }
            return leg;
        });
        setLegs(updatedLegs);
    };

    const removeLeg = (index) => {
        if (legs.length > 1) {
            setLegs(legs.filter((_, i) => i !== index));
        }
    };

    const toggleLegExpansion = (index) => {
        const updatedLegs = legs.map((leg, i) => {
            if (i === index) {
                return { ...leg, expanded: !leg.expanded };
            }
            return leg;
        });
        setLegs(updatedLegs);
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 2000));

            const jobPayload = {
                ...formData,
                legs,
                status: 'created',
                createdAt: new Date().toISOString(),
                estimatedCost,
                estimatedDuration
            };

            console.log('Job created:', jobPayload);
            alert(`Job ${formData.jobNumber} created successfully!`);
        } catch (error) {
            console.error('Error creating job:', error);
            alert('Error creating job. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Custom UI Components
    const Input = ({ label, error, helper, icon: Icon, className = '', ...props }) => (
        <div className="space-y-1">
            {label && <label className="block text-sm font-medium text-gray-700">{label}</label>}
            <div className="relative">
                {Icon && <Icon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />}
                <input
                    className={`w-full ${Icon ? 'pl-10' : ''} px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${error ? 'border-red-500' : ''} ${className}`}
                    {...props}
                />
            </div>
            {error && <p className="text-red-500 text-xs">{error}</p>}
            {helper && <p className="text-gray-500 text-xs">{helper}</p>}
        </div>
    );

    const Select = ({ label, options = [], error, helper, value, onChange, icon: Icon, ...props }) => (
        <div className="space-y-1">
            {label && <label className="block text-sm font-medium text-gray-700">{label}</label>}
            <div className="relative">
                {Icon && <Icon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 z-10" />}
                <select
                    value={value}
                    onChange={(e) => onChange && onChange(e.target.value)}
                    className={`w-full ${Icon ? 'pl-10' : ''} px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${error ? 'border-red-500' : ''}`}
                    {...props}
                >
                    {options.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </select>
            </div>
            {error && <p className="text-red-500 text-xs">{error}</p>}
            {helper && <p className="text-gray-500 text-xs">{helper}</p>}
        </div>
    );

    const Button = ({ children, variant = 'primary', loading, className = '', onClick, ...props }) => {
        const baseClasses = 'px-4 py-2 rounded-lg font-medium focus:outline-none focus:ring-2 transition-colors flex items-center justify-center';
        const variants = {
            primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
            secondary: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500',
            outline: 'border border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-500',
            ghost: 'text-gray-600 hover:bg-gray-100 focus:ring-gray-500',
            danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500'
        };

        return (
            <button
                className={`${baseClasses} ${variants[variant]} ${className}`}
                onClick={onClick}
                disabled={loading}
                {...props}
            >
                {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
                {children}
            </button>
        );
    };

    const Card = ({ children, className = '' }) => (
        <div className={`bg-white border border-gray-200 rounded-lg shadow-sm ${className}`}>
            {children}
        </div>
    );

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center">
                    <Button variant="ghost" onClick={() => window.history.back()} className="mr-4">
                        <ArrowLeft className="w-4 h-4" />
                    </Button>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Create New Job</h1>
                        <p className="text-gray-600">Complete logistics job setup with multi-leg routing and tracking</p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <Button
                        variant="outline"
                        onClick={() => {
                            const draftData = { formData, legs };
                            console.log('Draft saved:', draftData);
                            alert('Job saved as draft');
                        }}
                    >
                        <Save className="w-4 h-4 mr-2" />
                        Save Draft
                    </Button>
                    <Button onClick={handleSubmit} loading={loading} variant="secondary">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Create Job
                    </Button>
                </div>
            </div>

            <div className="space-y-8">
                {/* Job Information Section */}
                <Card className="p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                        <Package className="w-5 h-5 mr-2 text-blue-600" />
                        Job Information
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <Select
                            label="Job Type *"
                            icon={Package}
                            value={formData.type}
                            onChange={(value) => updateFormData('type', value)}
                            options={[
                                { value: 'import', label: 'Import' },
                                { value: 'export', label: 'Export' },
                                { value: 'domestic', label: 'Domestic' },
                                { value: 'vendor', label: 'Vendor' }
                            ]}
                        />

                        <Select
                            label="Priority *"
                            icon={AlertTriangle}
                            value={formData.priority}
                            onChange={(value) => updateFormData('priority', value)}
                            options={[
                                { value: 'low', label: 'Low Priority' },
                                { value: 'medium', label: 'Medium Priority' },
                                { value: 'high', label: 'High Priority' },
                                { value: 'urgent', label: 'Urgent' }
                            ]}
                        />

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Job Number</label>
                            <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg">
                                <span className="text-gray-900 font-medium flex items-center">
                                    <Hash className="w-4 h-4 mr-2 text-gray-400" />
                                    {formData.jobNumber}
                                </span>
                            </div>
                        </div>

                        <Input
                            label="Customer PO"
                            icon={FileText}
                            value={formData.customerPO}
                            onChange={(e) => updateFormData('customerPO', e.target.value)}
                            placeholder="PO-CUSTOMER-2025-001"
                        />
                    </div>

                    <div className="mt-6">
                        <Input
                            label="Job Description"
                            icon={Clipboard}
                            value={formData.description}
                            onChange={(e) => updateFormData('description', e.target.value)}
                            placeholder="Brief description of the job..."
                        />
                    </div>
                </Card>

                {/* Route & Legs Section */}
                <Card className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                            <MapIcon className="w-5 h-5 mr-2 text-blue-600" />
                            Route & Legs Configuration
                        </h2>
                        <Button onClick={addLeg} variant="outline">
                            <Plus className="w-4 h-4 mr-2" />
                            Add Leg
                        </Button>
                    </div>

                    <div className="space-y-4">
                        {legs.map((leg, index) => (
                            <div key={leg.id} className="border border-gray-200 rounded-lg overflow-hidden">
                                {/* Leg Header */}
                                <div
                                    className="bg-gray-50 p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                                    onClick={() => toggleLegExpansion(index)}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-4">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-medium ${leg.type === 'pickup' ? 'bg-green-500' :
                                                    leg.type === 'delivery' ? 'bg-blue-500' : 'bg-orange-500'
                                                }`}>
                                                {index + 1}
                                            </div>
                                            <div>
                                                <h3 className="font-medium text-gray-900 capitalize">
                                                    Leg {index + 1} - {leg.type}
                                                </h3>
                                                <p className="text-sm text-gray-600">
                                                    {leg.origin || 'Origin not set'} → {leg.destination || 'Destination not set'}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-3">
                                            <div className="text-sm text-gray-500">
                                                {leg.distance} km • {leg.estimatedHours} hrs
                                            </div>
                                            {legs.length > 1 && (
                                                <Button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        removeLeg(index);
                                                    }}
                                                    variant="ghost"
                                                    className="text-red-600 hover:text-red-700 p-2"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            )}
                                            {leg.expanded ?
                                                <ChevronUp className="w-5 h-5 text-gray-400" /> :
                                                <ChevronDown className="w-5 h-5 text-gray-400" />
                                            }
                                        </div>
                                    </div>
                                </div>

                                {/* Leg Content */}
                                {leg.expanded && (
                                    <div className="p-6 space-y-6">
                                        {/* Basic Information */}
                                        <div>
                                            <h4 className="font-medium text-gray-900 mb-4">Basic Information</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                <Select
                                                    label="Leg Type"
                                                    icon={MapIcon}
                                                    value={leg.type}
                                                    onChange={(value) => updateLeg(index, 'type', value)}
                                                    options={[
                                                        { value: 'pickup', label: 'Pickup' },
                                                        { value: 'delivery', label: 'Delivery' },
                                                        { value: 'transit', label: 'Transit' }
                                                    ]}
                                                />

                                                <Select
                                                    label="Vehicle Type"
                                                    icon={Truck}
                                                    value={leg.vehicleType}
                                                    onChange={(value) => updateLeg(index, 'vehicleType', value)}
                                                    options={vehicleTypes}
                                                />

                                                <Select
                                                    label="Goods Type"
                                                    icon={Package}
                                                    value={leg.goodsType}
                                                    onChange={(value) => updateLeg(index, 'goodsType', value)}
                                                    options={goodsTypes}
                                                />
                                            </div>
                                        </div>

                                        {/* Stakeholders */}
                                        <div>
                                            <h4 className="font-medium text-gray-900 mb-4">Stakeholders</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                                <Select
                                                    label="Customer"
                                                    icon={Users}
                                                    value={leg.customer}
                                                    onChange={(value) => updateLeg(index, 'customer', value)}
                                                    options={[{ value: '', label: 'Select Customer' }, ...customers]}
                                                />

                                                <Select
                                                    label="Vendor"
                                                    icon={Building2}
                                                    value={leg.vendor}
                                                    onChange={(value) => updateLeg(index, 'vendor', value)}
                                                    options={[{ value: '', label: 'Select Vendor' }, ...vendors]}
                                                />

                                                <Select
                                                    label="Port"
                                                    icon={Ship}
                                                    value={leg.port}
                                                    onChange={(value) => updateLeg(index, 'port', value)}
                                                    options={[{ value: '', label: 'Select Port' }, ...ports]}
                                                />

                                                <Select
                                                    label="Warehouse"
                                                    icon={Warehouse}
                                                    value={leg.warehouse}
                                                    onChange={(value) => updateLeg(index, 'warehouse', value)}
                                                    options={[{ value: '', label: 'Select Warehouse' }, ...warehouses]}
                                                />
                                            </div>
                                        </div>

                                        {/* Transport Details */}
                                        <div>
                                            <h4 className="font-medium text-gray-900 mb-4">Transport Details</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                <Select
                                                    label="Transporter"
                                                    icon={Truck}
                                                    value={leg.transporter}
                                                    onChange={(value) => updateLeg(index, 'transporter', value)}
                                                    options={[{ value: '', label: 'Select Transporter' }, ...transporters]}
                                                />

                                                <Select
                                                    label="Vehicle"
                                                    icon={Car}
                                                    value={leg.vehicle}
                                                    onChange={(value) => updateLeg(index, 'vehicle', value)}
                                                    options={[{ value: '', label: 'Select Vehicle' }, ...vehicles]}
                                                />

                                                <Select
                                                    label="Driver"
                                                    icon={User}
                                                    value={leg.driver}
                                                    onChange={(value) => updateLeg(index, 'driver', value)}
                                                    options={[{ value: '', label: 'Select Driver' }, ...drivers]}
                                                />
                                            </div>
                                        </div>

                                        {/* Container Details */}
                                        <div>
                                            <h4 className="font-medium text-gray-900 mb-4">Container Details</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                                <Select
                                                    label="Container"
                                                    icon={Container}
                                                    value={leg.container}
                                                    onChange={(value) => updateLeg(index, 'container', value)}
                                                    options={[{ value: '', label: 'Select Container' }, ...containers.map(c => ({
                                                        value: c.value,
                                                        label: `${c.label} (${c.type.toUpperCase()}) - ${c.owner}`
                                                    }))]}
                                                />

                                                {leg.container && (
                                                    <>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Container Type</label>
                                                            <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg">
                                                                <span className="text-gray-900 font-medium">
                                                                    {containers.find(c => c.value === leg.container)?.type.toUpperCase() || 'N/A'}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Free Time</label>
                                                            <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg">
                                                                <span className="text-gray-900 font-medium">
                                                                    {containers.find(c => c.value === leg.container)?.freeTime || 'N/A'} hours
                                                                </span>
                                                            </div>
                                                        </div>

                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Detention Rate</label>
                                                            <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg">
                                                                <span className="text-gray-900 font-medium">
                                                                    ₹{containers.find(c => c.value === leg.container)?.detentionRate || 'N/A'}/hour
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {/* Route Information */}
                                        <div>
                                            <h4 className="font-medium text-gray-900 mb-4">Route Information</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                <Input
                                                    label="Origin *"
                                                    icon={MapPin}
                                                    value={leg.origin}
                                                    onChange={(e) => updateLeg(index, 'origin', e.target.value)}
                                                    placeholder="Enter pickup location"
                                                />

                                                <Input
                                                    label="Destination *"
                                                    icon={Target}
                                                    value={leg.destination}
                                                    onChange={(e) => updateLeg(index, 'destination', e.target.value)}
                                                    placeholder="Enter delivery location"
                                                />
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                                <Input
                                                    label="Distance (km)"
                                                    icon={Ruler}
                                                    type="number"
                                                    value={leg.distance}
                                                    onChange={(e) => updateLeg(index, 'distance', e.target.value)}
                                                    placeholder="0"
                                                />

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Est. Duration</label>
                                                    <div className="px-3 py-2 bg-blue-50 border border-blue-300 rounded-lg">
                                                        <span className="text-blue-800 font-medium flex items-center">
                                                            <Timer className="w-4 h-4 mr-2" />
                                                            {leg.estimatedHours} hours
                                                        </span>
                                                    </div>
                                                </div>

                                                <Input
                                                    label="Journey Date"
                                                    icon={Calendar}
                                                    type="date"
                                                    value={leg.journeyDate}
                                                    onChange={(e) => updateLeg(index, 'journeyDate', e.target.value)}
                                                />

                                                <Input
                                                    label="Journey Time"
                                                    icon={Clock}
                                                    type="time"
                                                    value={leg.journeyTime}
                                                    onChange={(e) => updateLeg(index, 'journeyTime', e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        {/* Tracking & Monitoring */}
                                        <div>
                                            <h4 className="font-medium text-gray-900 mb-4">Tracking & Monitoring</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div className="space-y-4">
                                                    <label className="flex items-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={leg.gpsTracking}
                                                            onChange={(e) => updateLeg(index, 'gpsTracking', e.target.checked)}
                                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                        />
                                                        <Navigation className="w-4 h-4 ml-2 mr-2 text-gray-500" />
                                                        <span className="text-sm text-gray-700">GPS Tracking</span>
                                                    </label>

                                                    <label className="flex items-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={leg.photoUpdates}
                                                            onChange={(e) => updateLeg(index, 'photoUpdates', e.target.checked)}
                                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                        />
                                                        <Camera className="w-4 h-4 ml-2 mr-2 text-gray-500" />
                                                        <span className="text-sm text-gray-700">Photo Updates</span>
                                                    </label>

                                                    <label className="flex items-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={leg.geofencing}
                                                            onChange={(e) => updateLeg(index, 'geofencing', e.target.checked)}
                                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                        />
                                                        <Shield className="w-4 h-4 ml-2 mr-2 text-gray-500" />
                                                        <span className="text-sm text-gray-700">Geofencing</span>
                                                    </label>
                                                </div>

                                                <div className="space-y-4">
                                                    <label className="flex items-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={leg.temperatureMonitoring}
                                                            onChange={(e) => updateLeg(index, 'temperatureMonitoring', e.target.checked)}
                                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                        />
                                                        <Thermometer className="w-4 h-4 ml-2 mr-2 text-gray-500" />
                                                        <span className="text-sm text-gray-700">Temperature Monitoring</span>
                                                    </label>

                                                    {leg.temperatureMonitoring && (
                                                        <Input
                                                            label="Temperature Range"
                                                            icon={Thermometer}
                                                            value={leg.temperatureRange}
                                                            onChange={(e) => updateLeg(index, 'temperatureRange', e.target.value)}
                                                            placeholder="e.g., -5 to 25°C"
                                                            className="ml-6"
                                                        />
                                                    )}

                                                    <label className="flex items-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={leg.trackingRequired}
                                                            onChange={(e) => updateLeg(index, 'trackingRequired', e.target.checked)}
                                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                        />
                                                        <Bell className="w-4 h-4 ml-2 mr-2 text-gray-500" />
                                                        <span className="text-sm text-gray-700">Real-time Alerts</span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Special Instructions */}
                                        <div>
                                            <Input
                                                label="Special Instructions"
                                                icon={Clipboard}
                                                value={leg.specialInstructions}
                                                onChange={(e) => updateLeg(index, 'specialInstructions', e.target.value)}
                                                placeholder="Enter any special handling instructions for this leg..."
                                            />
                                        </div>

                                        {/* Selected Information Display */}
                                        {(leg.customer || leg.transporter || leg.vehicle || leg.driver) && (
                                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                                <h5 className="font-medium text-blue-900 mb-3">Selected Details</h5>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                                    {leg.customer && (
                                                        <div>
                                                            <span className="text-blue-700 font-medium">Customer: </span>
                                                            <span className="text-blue-800">
                                                                {customers.find(c => c.value === leg.customer)?.label}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {leg.transporter && (
                                                        <div>
                                                            <span className="text-blue-700 font-medium">Transporter: </span>
                                                            <span className="text-blue-800">
                                                                {transporters.find(t => t.value === leg.transporter)?.label}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {leg.vehicle && (
                                                        <div>
                                                            <span className="text-blue-700 font-medium">Vehicle: </span>
                                                            <span className="text-blue-800">
                                                                {vehicles.find(v => v.value === leg.vehicle)?.label} - {vehicles.find(v => v.value === leg.vehicle)?.type}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {leg.driver && (
                                                        <div>
                                                            <span className="text-blue-700 font-medium">Driver: </span>
                                                            <span className="text-blue-800">
                                                                {drivers.find(d => d.value === leg.driver)?.label} - {drivers.find(d => d.value === leg.driver)?.phone}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Route Summary */}
                    <div className="border-t border-gray-200 pt-6 mt-6">
                        <h3 className="font-medium text-gray-900 mb-4">Route Summary</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-blue-600 font-medium">Total Legs</p>
                                        <p className="text-2xl font-bold text-blue-800">{legs.length}</p>
                                    </div>
                                    <MapIcon className="w-8 h-8 text-blue-600" />
                                </div>
                            </div>

                            <div className="bg-green-50 p-4 rounded-lg">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-green-600 font-medium">Total Distance</p>
                                        <p className="text-2xl font-bold text-green-800">
                                            {legs.reduce((sum, leg) => sum + (leg.distance || 0), 0)} km
                                        </p>
                                    </div>
                                    <Ruler className="w-8 h-8 text-green-600" />
                                </div>
                            </div>

                            <div className="bg-orange-50 p-4 rounded-lg">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-orange-600 font-medium">Est. Duration</p>
                                        <p className="text-2xl font-bold text-orange-800">{estimatedDuration}h</p>
                                    </div>
                                    <Timer className="w-8 h-8 text-orange-600" />
                                </div>
                            </div>

                            {/* <div className="bg-purple-50 p-4 rounded-lg">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-purple-600 font-medium">Est. Cost</p>
                                        <p className="text-2xl font-bold text-purple-800">₹{estimatedCost.toLocaleString()}</p>
                                    </div>
                                    <DollarSign className="w-8 h-8 text-purple-600" />
                                </div>
                            </div> */}
                        </div>
                    </div>
                </Card>

                {/* Pre-submission Checklist */}
                <Card className="p-6 bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200">
                    <div className="flex items-start">
                        <CheckCircle className="w-6 h-6 text-green-600 mt-0.5 mr-4" />
                        <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 mb-3">Job Creation Summary</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                    <ul className="space-y-2 text-gray-700">
                                        <li className="flex items-center">
                                            <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                                            Job type: <span className="font-medium ml-1 capitalize">{formData.type}</span>
                                        </li>
                                        <li className="flex items-center">
                                            <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                                            Priority: <span className="font-medium ml-1 capitalize">{formData.priority}</span>
                                        </li>
                                        <li className="flex items-center">
                                            <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                                            Total legs configured: <span className="font-medium ml-1">{legs.length}</span>
                                        </li>
                                        <li className="flex items-center">
                                            <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                                            Tracking enabled: <span className="font-medium ml-1">
                                                {legs.filter(leg => leg.trackingRequired).length} of {legs.length} legs
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                                <div>
                                    <ul className="space-y-2 text-gray-700">
                                        <li className="flex items-center">
                                            <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                                            Temperature monitoring: <span className="font-medium ml-1">
                                                {legs.filter(leg => leg.temperatureMonitoring).length} legs enabled
                                            </span>
                                        </li>
                                        <li className="flex items-center">
                                            <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                                            GPS tracking: <span className="font-medium ml-1">
                                                {legs.filter(leg => leg.gpsTracking).length} legs enabled
                                            </span>
                                        </li>
                                        {/* <li className="flex items-center">
                                            <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                                            Estimated total cost: <span className="font-medium ml-1">₹{estimatedCost.toLocaleString()}</span>
                                        </li> */}
                                        <li className="flex items-center">
                                            <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                                            Ready for submission
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default CreateJobPage;